import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ProjectInterface } from "../../../../models/project.model";
import { useEffect, useState } from "react";
import { Button } from "../../../../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../../components/ui/tabs";
import { Users, UserPlus, ArrowLeft } from "lucide-react";
import CurrentMembersTab from "./CurrentMembersTab";
import AddMembersTab from "./AddMembersTab";
import { getProjectById } from "@/api/project";
import { Card, CardHeader, CardTitle } from "../../../../components/ui/card";

const ProjectMembers: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const itemId = searchParams.get("id") || "";

    const { data: project, isLoading } = useQuery({
        queryKey: ['project', itemId],
        queryFn: () => getProjectById(itemId),
    });

    const [activeTab, setActiveTab] = useState("members");

    useEffect(() => {
        if ((!project || !itemId) && !isLoading) {
            navigate('/project');
        }
    }, [project, navigate, itemId, isLoading]);

    if (isLoading) {
        return <div className="flex justify-center items-center h-screen">Loading...</div>;
    }

    return (
        <div className="container mx-auto py-4 sm:py-6 px-2 sm:px-4 space-y-4 sm:space-y-6">
            <Card>
                <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-2 sm:space-y-0 pb-2">
                    <div className="space-y-1">
                        <CardTitle className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                                <ArrowLeft className="h-4 w-4" />
                            </Button>
                            Project Members
                        </CardTitle>
                        <p className="text-sm text-muted-foreground">
                            {project?.name}
                        </p>
                    </div>
                </CardHeader>
            </Card>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full h-[max-content] grid-cols-2 bg-white p-1 sm:p-2 rounded-xl border border-gray-200 shadow-sm">
                    <TabsTrigger
                        value="members"
                        className="flex items-center justify-center gap-1 sm:gap-2 px-3 sm:px-6 py-2 sm:py-3 rounded-lg transition-all duration-200 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 data-[state=active]:shadow-sm hover:bg-gray-50"
                    >
                        <Users className="h-4 w-4 sm:h-5 sm:w-5" />
                        <span className="text-sm sm:text-base font-medium">Current Members</span>
                    </TabsTrigger>
                    <TabsTrigger
                        value="add"
                        className="flex items-center justify-center gap-1 sm:gap-2 px-3 sm:px-6 py-2 sm:py-3 rounded-lg transition-all duration-200 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 data-[state=active]:shadow-sm hover:bg-gray-50"
                    >
                        <UserPlus className="h-4 w-4 sm:h-5 sm:w-5" />
                        <span className="text-sm sm:text-base font-medium">Add New Members</span>
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="members" className="mt-4">
                    <CurrentMembersTab
                        projectId={itemId}
                        onAddMembers={() => setActiveTab("add")}
                    />
                </TabsContent>

                <TabsContent value="add" className="mt-4">
                    <AddMembersTab
                        projectId={itemId}
                        project={project as ProjectInterface}
                        onViewMembers={() => setActiveTab("members")}
                    />
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default ProjectMembers;